import { create } from '@bufbuild/protobuf';
import useCloudService from '@grpc/composables/useCloudService';
import type { Datacenter } from 'gpcore-grpc/gpcore/api/cloud/v1/region_pb';
import {
  ListCountriesRequestSchema,
  ListDatacentersRequestSchema,
} from 'gpcore-grpc/gpcore/api/cloud/v1/requests_pb';
import {
  type ListCountriesResponse,
  type ListDatacentersResponse,
} from 'gpcore-grpc/gpcore/api/cloud/v1/responses_pb';
import type { Country } from 'gpcore-grpc/gpcore/type/v1/country_pb';
import { defineStore } from 'pinia';
import { reactive, toRefs } from 'vue';

export interface LocationStoreState {
  countries: Array<Country>;
  datacenters: Array<Datacenter>;
}

export const useLocationStore = defineStore('location', () => {
  const cloudService = useCloudService();

  const state = reactive<LocationStoreState>({
    countries: new Array<Country>(),
    datacenters: new Array<Datacenter>(),
  });

  const addDatacenters = (datacenters: Array<Datacenter>) => {
    datacenters.sort((a: Datacenter, b: Datacenter) => {
      if (a.region!.countryCode < b.region!.countryCode) {
        return -1;
      }
      if (a.region!.countryCode > b.region!.countryCode) {
        return 1;
      }
      return 0;
    });

    state.datacenters = datacenters;
  };

  const reloadCountries = async (): Promise<ListCountriesResponse> => {
    const request = create(ListCountriesRequestSchema);
    const { response, error } = await cloudService.executeApiCall('listCountries', request);
    if (error != null) throw error;

    state.countries = response.countries;

    return response;
  };

  const reloadDatacenters = async (): Promise<ListDatacentersResponse> => {
    const request = create(ListDatacentersRequestSchema);
    const { response, error } = await cloudService.executeApiCall('listDatacenters', request);
    if (error != null) throw error;

    state.datacenters = [];
    addDatacenters(response.datacenters);
    return response;
  };

  return {
    ...toRefs(state),
    reloadCountries,
    reloadDatacenters,
  };
});

export default useLocationStore;
